import React, { useState } from 'react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { MoreVertical, Pencil, CheckCircle2, PauseCircle, Trash2, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../utils/api';

const ProjectActionsMenu = ({ project, onEdit, onRefresh }) => {
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const handleStatusChange = async (newStatus) => {
    try {
      await api.patch(`/projects/${project.project_id}/status`, { status: newStatus });
      toast.success(`Project ${newStatus === 'completed' ? 'marked as complete' : newStatus === 'on_hold' ? 'put on hold' : 'updated'}`);
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update project status');
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await api.delete(`/projects/${project.project_id}`);
      toast.success('Project deleted successfully');
      setShowDeleteDialog(false);
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete project');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button 
            variant="ghost" 
            size="sm" 
            className="h-8 w-8 p-0 hover:bg-stone-100 rounded-lg"
            onClick={(e) => e.preventDefault()}
            data-testid={`project-menu-${project.project_id}`}
          >
            <MoreVertical className="h-4 w-4 text-stone-500" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-48 rounded-lg shadow-lg border-stone-200">
          <DropdownMenuItem 
            onClick={(e) => {
              e.preventDefault();
              onEdit(project);
            }}
            className="cursor-pointer"
          >
            <Pencil className="h-4 w-4 mr-2 text-stone-500" />
            Edit Project
          </DropdownMenuItem>
          
          <DropdownMenuSeparator />
          
          {project.status !== 'completed' && (
            <DropdownMenuItem 
              onClick={(e) => {
                e.preventDefault();
                handleStatusChange('completed');
              }}
              className="cursor-pointer"
            >
              <CheckCircle2 className="h-4 w-4 mr-2 text-green-600" />
              Mark as Complete
            </DropdownMenuItem>
          )}
          
          {project.status !== 'on_hold' ? (
            <DropdownMenuItem 
              onClick={(e) => {
                e.preventDefault();
                handleStatusChange('on_hold');
              }}
              className="cursor-pointer"
            >
              <PauseCircle className="h-4 w-4 mr-2 text-orange-600" />
              Put on Hold
            </DropdownMenuItem>
          ) : (
            <DropdownMenuItem 
              onClick={(e) => {
                e.preventDefault();
                handleStatusChange('active');
              }}
              className="cursor-pointer"
            >
              <CheckCircle2 className="h-4 w-4 mr-2 text-blue-600" />
              Resume Project
            </DropdownMenuItem>
          )}
          
          <DropdownMenuSeparator />
          
          <DropdownMenuItem 
            onClick={(e) => {
              e.preventDefault();
              setShowDeleteDialog(true);
            }}
            className="cursor-pointer text-red-600 focus:text-red-600 focus:bg-red-50"
          >
            <Trash2 className="h-4 w-4 mr-2" />
            Delete Project
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent className="rounded-lg">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center text-red-600">
              <AlertTriangle className="h-5 w-5 mr-2" />
              Delete Project
            </AlertDialogTitle>
            <AlertDialogDescription className="text-stone-600">
              Are you sure you want to delete <span className="font-semibold text-stone-900">"{project.project_name}"</span>? 
              This action cannot be undone and will permanently remove all project data including:
              <ul className="mt-2 ml-4 list-disc text-sm">
                <li>All milestones and timeline</li>
                <li>All documents and uploads</li>
                <li>All materials and approvals</li>
                <li>All payment records</li>
                <li>All change requests</li>
              </ul>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-lg" disabled={deleting}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700 text-white rounded-lg"
            >
              {deleting ? 'Deleting...' : 'Delete Project'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default ProjectActionsMenu;
